import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSellers() {
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', phone: '', email: '', active: true });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try { const r = await api.get('/admin/sellers'); setSellers(r.data); }
        catch (e) { console.error(e); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm({ name: '', phone: '', email: '', active: true }); setDialogOpen(true); };
    const openEdit = (s) => { setEditing(s.id); setForm({ name: s.name, phone: s.phone, email: s.email || '', active: s.active }); setDialogOpen(true); };

    const handleSave = async () => {
        if (!form.name || !form.phone) { toast.error('Nombre y telefono son obligatorios'); return; }
        setSaving(true);
        try {
            if (editing) { await api.put(`/admin/sellers/${editing}`, form); toast.success('Vendedor actualizado'); }
            else { await api.post('/admin/sellers', form); toast.success('Vendedor creado'); }
            setDialogOpen(false); load();
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Eliminar este vendedor?')) return;
        try { await api.delete(`/admin/sellers/${id}`); toast.success('Vendedor eliminado'); load(); }
        catch { toast.error('Error al eliminar'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-sellers">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-heading font-bold text-cp-text-main">Vendedores</h1>
                <button onClick={openNew} data-testid="add-seller-btn" className="flex items-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white px-4 py-2.5 rounded-xl text-sm font-semibold">
                    <Plus size={16} /> Agregar
                </button>
            </div>

            <p className="text-cp-text-muted text-sm">Los vendedores activos recibiran notificaciones WhatsApp de cada venta realizada.</p>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sellers.map(s => (
                    <div key={s.id} data-testid={`seller-card-${s.id}`} className="bg-white border border-cp-border rounded-xl p-4">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="font-heading font-bold text-cp-text-main">{s.name}</h3>
                                <span className={`text-xs font-semibold ${s.active ? 'text-cp-success' : 'text-cp-error'}`}>
                                    {s.active ? 'Activo' : 'Inactivo'}
                                </span>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => openEdit(s)} className="p-1.5 text-cp-text-muted hover:text-cp-secondary rounded-lg hover:bg-cp-bg-alt"><Pencil size={14} /></button>
                                <button onClick={() => handleDelete(s.id)} className="p-1.5 text-cp-text-muted hover:text-cp-error rounded-lg hover:bg-cp-bg-alt"><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <div className="space-y-1.5 text-sm text-cp-text-body">
                            <div className="flex items-center gap-2"><Phone size={14} className="text-cp-primary" />{s.phone}</div>
                            {s.email && <div className="flex items-center gap-2"><Mail size={14} className="text-cp-primary" />{s.email}</div>}
                        </div>
                    </div>
                ))}
                {!loading && sellers.length === 0 && (
                    <div className="col-span-full text-center py-12 text-cp-text-muted">
                        No hay vendedores registrados
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle className="font-heading">{editing ? 'Editar Vendedor' : 'Nuevo Vendedor'}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="text-sm mb-1.5 block">Nombre *</Label>
                            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="seller-form-name" />
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">Telefono WhatsApp *</Label>
                            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+5491112345678" data-testid="seller-form-phone" />
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">Email</Label>
                            <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="seller-form-email" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} />
                            <Label className="text-sm">Activo</Label>
                        </div>
                        <button onClick={handleSave} disabled={saving} data-testid="seller-form-save"
                            className="w-full bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white py-3 rounded-xl font-semibold">
                            {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear Vendedor'}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
